import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/features/auth/hooks';
import {
  useAccountQuery,
  useDpaCurrentQuery,
  useAcceptDpaMutation,
} from '@/features/settings/account-api';

/*
 * DpaBanner (Sessio 15, GDPR-B + Article 28).
 *
 * Renderoidaan AppLayout:n paaalueen ylaosaan (EmailVerifyBanner ja TrialBanner:n
 * jalkeen) kun kayttaja on kirjautunut JA users.dpa_accepted_version ei vastaa
 * DPA_CURRENT.version:ia.
 *
 * Toiminta:
 *  - Linkki uuteen DPA-sisaltoon (content_url, target="_blank")
 *  - "Hyvaksyn" -nappi -> POST /api/me/dpa/accept -> banner katoaa automaattisesti
 *    (useAccountQuery invalidoituu mutaation onnistumisen jalkeen)
 *
 * Vaihe 1:ssa pelka banneri, EI lukkoa middlewaressa (kayttaja voi jatkaa
 * sovelluksen kayttoa). Vaihe 2:ssa voidaan lisata middleware-lukko jos
 * tarpeen.
 */
export function DpaBanner() {
  const { t } = useTranslation();
  const { status } = useAuth();
  const accountQuery = useAccountQuery();
  const dpaQuery = useDpaCurrentQuery();
  const acceptMutation = useAcceptDpaMutation();

  // Naytetaan vain kirjautuneille jotka eivat ole vahvistaneet nykyista DPA:ta
  if (status !== 'authenticated') return null;
  if (!accountQuery.data || !dpaQuery.data) return null;
  if (accountQuery.data.dpa_accepted_version === dpaQuery.data.version) return null;

  const handleAccept = () => {
    acceptMutation.mutate({ version: dpaQuery.data!.version });
  };

  return (
    <div
      role="alert"
      aria-label={t('dpa.banner.title')}
      className="flex flex-col gap-2 border-b border-oranssi bg-oranssi/10 px-6 py-3 text-sm text-oranssi sm:flex-row sm:items-center sm:justify-between lg:px-10"
      data-testid="dpa-banner"
    >
      <div className="flex-1">
        <strong className="mr-2">{t('dpa.banner.title')}</strong>
        <span>
          {t('dpa.banner.message', { version: dpaQuery.data.version })}{' '}
          <a
            href={dpaQuery.data.content_url}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:no-underline"
            data-testid="dpa-banner-link"
          >
            {t('dpa.banner.readLink')}
          </a>
        </span>
      </div>
      <Button
        variant="secondary"
        size="sm"
        onClick={handleAccept}
        disabled={acceptMutation.isPending}
        data-testid="dpa-banner-accept"
      >
        {acceptMutation.isPending
          ? t('dpa.banner.accepting')
          : t('dpa.banner.acceptButton')}
      </Button>
    </div>
  );
}
